/*!
 * Extenso.js - Implementação interna baseada em extenso@2.0.1 (MIT License)
 * Original: © 2015-2019 Matheus Alves
 * Reescrito como ES module para eliminar eval() do bundle webpack original.
 */

// --- Vocabulário ---

const UNITS = ['zero', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];

const TEENS = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];

const TENS = ['vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];

const HUNDREDS = ['cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];

// Escalas: [singular, plural]
const SCALES = [
  ['mil', 'mil'],
  ['milhão', 'milhões'],
  ['bilhão', 'bilhões'],
  ['trilhão', 'trilhões'],
  ['quatrilhão', 'quatrilhões'],
  ['quintilhão', 'quintilhões'],
  ['sextilhão', 'sextilhões'],
  ['septilhão', 'septilhões'],
  ['octilhão', 'octilhões'],
  ['nonilhão', 'nonilhões'],
  ['decilhão', 'decilhões'],
  ['undecilhão', 'undecilhões'],
  ['duodecilhão', 'duodecilhões'],
];

const CURRENCIES = {
  BRL: { singular: 'real', plural: 'reais', subunit: { singular: 'centavo', plural: 'centavos' } },
  EUR: { singular: 'euro', plural: 'euros', subunit: { singular: 'cêntimo', plural: 'cêntimos' } },
};

// --- Escrita de números ---

function writeLt10(n) {
  return UNITS[n];
}

function writeLt100(n) {
  if (n < 10) return writeLt10(n);
  if (n < 20) return TEENS[n - 10];
  const unit = n % 10;
  const ten = TENS[Math.floor(n / 10) - 2];
  return unit !== 0 ? `${ten} e ${writeLt10(unit)}` : ten;
}

function writeLt1000(n) {
  if (n < 100) return writeLt100(n);
  if (n === 100) return 'cem';
  const hundred = HUNDREDS[Math.floor(n / 100) - 1];
  const rest = n % 100;
  return rest !== 0 ? `${hundred} e ${writeLt100(rest)}` : hundred;
}

function splitIntoGroups(intStr) {
  const groups = [];
  let str = intStr;
  while (str.length > 3) {
    groups.unshift(str.slice(-3));
    str = str.slice(0, -3);
  }
  groups.unshift(str);
  return groups.map(g => parseInt(g, 10));
}

function writeInt(intStr, gender) {
  const n = parseInt(intStr, 10);

  if (n === 0) return 'zero';
  if (n < 1000) {
    const text = writeLt1000(n);
    return gender === 'f' ? toFemale(text) : text;
  }
  if (n === 1000) return 'mil';

  const groups = splitIntoGroups(intStr.replace(/^0+/, '') || '0');
  const parts = [];
  for (let i = 0; i < groups.length; i++) {
    const val = groups[i];
    if (val === 0) continue;

    const groupScaleIdx = groups.length - 2 - i;

    if (groupScaleIdx < 0) {
      // Unidades (último grupo)
      parts.push({ text: writeLt1000(val), val, isLast: true, scale: null });
    } else if (groupScaleIdx === 0) {
      // Mil
      if (val === 1) {
        parts.push({ text: 'mil', val, isLast: false, scale: 'mil' });
      } else {
        parts.push({ text: `${writeLt1000(val)} mil`, val, isLast: false, scale: 'mil' });
      }
    } else {
      // Milhão+
      const [sing, plur] = SCALES[groupScaleIdx];
      const scaleName = val === 1 ? sing : plur;
      parts.push({ text: `${writeLt1000(val)} ${scaleName}`, val, isLast: false, scale: scaleName });
    }
  }

  // Montar com conjunções
  const lastGroup = groups[groups.length - 1];
  const result = [];

  for (let i = 0; i < parts.length; i++) {
    if (i > 0) {
      const isLastPart = i === parts.length - 1;
      if (isLastPart && (lastGroup < 100 || lastGroup % 100 === 0)) {
        result.push(' e ');
      } else if (isLastPart) {
        result.push(' ');
      } else {
        result.push(', ');
      }
    }
    result.push(parts[i].text);
  }

  const text = result.join('');
  return gender === 'f' ? toFemale(text) : text;
}

function toFemale(text) {
  return text.replace(/\bum\b/g, 'uma').replace(/\bdois\b/g, 'duas');
}

// --- Percentual ---

const FRACTIONAL_BASES = [
  ['milésimo', 'milésimos'],
  ['milionésimo', 'milionésimos'],
  ['bilionésimo', 'bilionésimos'],
  ['trilionésimo', 'trilionésimos'],
  ['quatrilionésimo', 'quatrilionésimos'],
  ['quintilionésimo', 'quintilionésimos'],
  ['sextilionésimo', 'sextilionésimos'],
  ['septilionésimo', 'septilionésimos'],
  ['octilionésimo', 'octilionésimos'],
  ['nonilionésimo', 'nonilionésimos'],
  ['decilionésimo', 'decilionésimos'],
  ['undecilionésimo', 'undecilionésimos'],
  ['duodecilionésimo', 'duodecilionésimos'],
];

function getFractionalUnit(decPlaces, decNum) {
  if (decPlaces === 0) return '';
  const group = Math.floor((decPlaces - 1) / 3);
  const pos = (decPlaces - 1) % 3;
  const singular = decNum === 1;

  if (pos === 0) {
    const base = group === 0 ? 'décimo' : `décimo de ${FRACTIONAL_BASES[group - 1][0]}`;
    return singular ? base : base.replace('décimo', 'décimos');
  }
  if (pos === 1) {
    const base = group === 0 ? 'centésimo' : `centésimo de ${FRACTIONAL_BASES[group - 1][0]}`;
    return singular ? base : base.replace('centésimo', 'centésimos');
  }
  const [sing, plur] = FRACTIONAL_BASES[group];
  return singular ? sing : plur;
}

function writePercent(intStr, decStr, gender, isNegative, negativeStyle) {
  const intNum = parseInt(intStr, 10);
  const decNum = parseInt(decStr, 10);
  const decPlaces = decStr.length;

  const intText = writeInt(intStr, gender);

  let coreText;

  if (decNum === 0) {
    coreText = intText;
  } else {
    const decText = writeInt(decStr, gender);
    const unit = getFractionalUnit(decPlaces, decNum);

    if (intNum === 0) {
      coreText = `${decText} ${unit}`;
    } else {
      coreText = `${intText} inteiros e ${decText} ${unit}`;
    }
  }

  if (isNegative && (intNum + decNum > 0)) {
    if (negativeStyle === 'informal') {
      coreText = `menos ${coreText}`;
    } else {
      coreText = `${coreText} negativo`;
    }
  }

  return `${coreText} por cento`;
}

// --- Moeda ---

function writeCurrency(iso, intStr, decStr) {
  const opts = CURRENCIES[iso] || CURRENCIES.BRL;
  const intNum = parseInt(intStr, 10);
  const decNum = parseInt(decStr, 10);

  const intText = writeInt(intStr);
  const decText = writeInt(decStr);

  let unitText = '';
  if (intNum === 1) {
    unitText = `${intText} ${opts.singular}`;
  } else if (intNum >= 1000000 && intNum % 1000000 === 0) {
    unitText = `${intText} de ${opts.plural}`;
  } else {
    unitText = `${intText} ${opts.plural}`;
  }

  let subunitText = '';
  if (decNum === 1) {
    subunitText = `${decText} ${opts.subunit.singular}`;
  } else {
    subunitText = `${decText} ${opts.subunit.plural}`;
  }

  if (intNum === 0 && decNum === 0) return `zero ${opts.plural}`;
  if (intNum === 0) return subunitText;
  if (decNum === 0) return unitText;
  return `${unitText} e ${subunitText}`;
}

// --- Validação e parsing ---

function parseNumber(num) {
  let str = String(num).trim();

  const isNegative = str.startsWith('-');
  if (isNegative) str = str.substring(1);

  // Remove separadores de milhar (ponto)
  str = str.replace(/\./g, '');

  // Separa inteiro e decimal pela vírgula
  const parts = str.split(',');
  const integer = parts[0] || '0';
  const decimal = parts.length > 1 ? parts[1] : '0';

  return { isNegative, integer, decimal };
}

function isValidNumber(val) {
  const str = String(val).trim();
  return /^-?(\d{1,3}(\.\d{3})*|\d+)(,\d+)?$/.test(str);
}

// --- Função principal ---

function extenso(num, opts = {}) {
  const str = String(num).trim();

  if (!isValidNumber(str)) {
    throw new TypeError(`O valor "${str}" não é um número válido.`);
  }

  const mode = opts.mode || 'number';
  const negative = opts.negative || 'formal';
  const gender = opts.number?.gender || 'm';
  const currencyType = opts.currency?.type || 'BRL';

  const { isNegative, integer, decimal } = parseNumber(str);

  let result;

  if (mode === 'currency') {
    const decPadded = decimal.length === 1 ? decimal + '0' : decimal.substring(0, 2);
    result = writeCurrency(currencyType, integer, decPadded);
  } else if (mode === 'percentual') {
    result = writePercent(integer, decimal, gender, isNegative, negative);
  } else {
    const intText = writeInt(integer, gender);
    if (decimal !== '0' && parseInt(decimal, 10) !== 0) {
      const decText = writeInt(decimal, gender);
      result = `${intText} vírgula ${decText}`;
    } else {
      result = intText;
    }
  }

  if (mode !== 'percentual' && isNegative && parseInt(integer, 10) + parseInt(decimal, 10) > 0) {
    if (negative === 'informal') {
      result = `menos ${result}`;
    } else {
      result = `${result} negativo`;
    }
  }

  return result;
}

export default extenso;
